import { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Camera, RefreshCw, X } from 'lucide-react';

// Live in-app selfie camera — front camera only, NO gallery access.
// Falls back to a capture-forced file input only when getUserMedia is
// completely unsupported (very old browsers).
export const SelfieCameraDialog = ({ open, onClose, onCapture }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fallbackRef = useRef(null);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const supported = !!navigator.mediaDevices?.getUserMedia;

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const start = useCallback(async () => {
    setError(null);
    setReady(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setReady(true);
    } catch (e) {
      setError('Camera access is required to clock in/out. Please allow camera permission and try again.');
    }
  }, []);

  useEffect(() => {
    if (open && supported) start();
    if (!open) stop();
    return stop;
  }, [open, supported, start, stop]);

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    stop();
    onCapture(dataUrl);
    onClose();
  };

  const onFallbackFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => { onCapture(reader.result); onClose(); };
    reader.readAsDataURL(file);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col items-center justify-center p-4" data-testid="selfie-camera-dialog">
      <button
        onClick={() => { stop(); onClose(); }}
        className="absolute top-4 right-4 p-2 text-white/70 hover:text-white"
        data-testid="selfie-camera-close"
      >
        <X className="w-6 h-6" />
      </button>

      {!supported ? (
        <div className="text-center space-y-4">
          <p className="text-sm text-white/80 max-w-xs">Your browser does not support the in-app camera. Use the button below — it opens your phone camera directly.</p>
          <input ref={fallbackRef} type="file" accept="image/*" capture="user" className="hidden" onChange={onFallbackFile} />
          <Button onClick={() => fallbackRef.current?.click()} className="bg-primary text-primary-foreground rounded-sm uppercase" data-testid="selfie-fallback-btn">
            <Camera className="w-4 h-4 mr-2" /> Open Camera
          </Button>
        </div>
      ) : error ? (
        <div className="text-center space-y-4">
          <p className="text-sm text-red-400 max-w-xs" data-testid="selfie-camera-error">{error}</p>
          <Button onClick={start} variant="outline" className="rounded-sm uppercase" data-testid="selfie-camera-retry">
            <RefreshCw className="w-4 h-4 mr-2" /> Try again
          </Button>
        </div>
      ) : (
        <>
          <p className="text-white/80 text-xs uppercase tracking-widest mb-3">Position your face in the frame</p>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full max-w-sm rounded-md border border-primary/40 aspect-[3/4] object-cover"
            style={{ transform: 'scaleX(-1)' }}
            data-testid="selfie-camera-video"
          />
          <Button
            onClick={capture}
            disabled={!ready}
            className="mt-4 w-full max-w-sm h-14 bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm text-lg font-barlow uppercase tracking-widest"
            data-testid="selfie-capture-btn"
          >
            <Camera className="w-6 h-6 mr-2" /> Capture Selfie
          </Button>
        </>
      )}
    </div>
  );
};
